import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { ImportProposalsSearch } from 'src/components/ImportProposalsModal/import-proposals-search';
import { PortalioProposalsList } from 'src/components/ImportProposalsModal/portalio-proposals-list';
import { useImportPortalioProject } from 'src/graphql/entities/integrations/portalio/hooks/use-import-portalio-project';
import { Modals, useModalMethods } from 'src/store/modals';
import { ImportResults } from './import-results';
import { ProjectsSummary } from './projects-summary';

export const ImportPortalioProjectModal = () => {
  const { closeModal } = useModalMethods(Modals.ImportPortalioProjects);

  const [currentView, setCurrentView] = useState<
    'SELECT_ITEMS_VIEW' | 'PREVIEW' | 'COMPLETED'
  >('SELECT_ITEMS_VIEW');
  const [searchValue, setSearchValue] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<
    { id: number; name: string }[]
  >([]);

  const selectedCount = selectedProjects.length;
  const selectedLabel = selectedCount
    ? `${selectedCount} ${selectedCount === 1 ? 'project' : 'projects'} selected`
    : 'No projects selected';

  const { importPortalioProject, loading: importLoading, records, errors } =
    useImportPortalioProject();

  const handleOnImport = useCallback(async () => {
    await importPortalioProject(selectedProjects.map((p) => p.id));
    setCurrentView('COMPLETED');
  }, [importPortalioProject, selectedProjects]);

  if (currentView === 'SELECT_ITEMS_VIEW') {
    return (
      <Dialog
        open
        onClose={closeModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Import from Portal.io</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Select projects to import
            </Typography>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stack spacing={2}>
            <ImportProposalsSearch
              onChange={setSearchValue}
              placeholder="Search projects..."
            />
            <PortalioProposalsList
              filterText={searchValue}
              onProjectsSelected={setSelectedProjects}
              showSelectAll
              initialSelectedIds={selectedProjects.map((p) => p.id)}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedLabel}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setCurrentView('PREVIEW')}
              disabled={selectedProjects.length === 0}
            >
              {`Review ${selectedProjects.length} ${selectedProjects.length === 1 ? 'item' : 'items'} →`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    );
  }

  if (currentView === 'PREVIEW') {
    return (
      <ProjectsSummary
        projects={selectedProjects}
        onCancelClick={closeModal}
        onBackClick={() => setCurrentView('SELECT_ITEMS_VIEW')}
        onImportClick={handleOnImport}
        disableImportButton={importLoading}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={closeModal}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            Import Complete
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>
        <ImportResults
          records={records}
          loading={importLoading}
          projectIds={selectedProjects.map((p) => p.id)}
          errors={errors}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={() => setCurrentView('SELECT_ITEMS_VIEW')}>Import More</Button>
        <Button variant="contained" onClick={closeModal}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
